import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { api } from '../api';
import { ApiError, type PackItem, type TrajeCor, type TrajeDoc, type TrajeDto, type TrajeEscolha, type TrajeExclusiva, type TrajeMostrar } from '../api/types';
import { iconePara } from '../assets/items';
import { t } from '../i18n/idioma';
import { nomeDaCorDoTraje, nomeDaPecaDoTraje, nomeDoItem, nomeDoLugarDoTraje } from '../i18n/nomes';
import { useFechar } from '../input/useAtalhos';
import { corDaMatiz, degradeDaMatiz, iconeDoTraje, quandoTrajeCarregar } from '../look/traje';
import { clothDoTraje } from '../look/trajeVisual';
import { GumpWindow } from './GumpWindow';
import { DIRECOES_DO_PROVADOR, porRaridade, RARIDADE_DO_SET } from './guardaRoupa';
import { IcGuardaRoupa } from './icones/IconesV3';

interface Props {
  dead: boolean;
  /** O provador do motor: pinta o seu boneco com o fio na direção. Devolve quem desfaz. */
  provador(canvas: HTMLCanvasElement, cloth: string, dir: number, feminino?: boolean): () => void;
  /** Depois de vestir ou retirar: o visual novo, para o seu boneco no mundo. */
  onVisual(cloth: string | null): void;
  /** Linha no diário e a mochila relida. */
  onChanged(text: string): void;
  onClose(): void;
}

const NATURAL = 'natural';
const VAZIA: TrajeEscolha = { peca: null, cor: NATURAL };
const SEM_MOSTRAR: TrajeMostrar = { elmo: false, peito: false, luvas: false, pernas: false };
/** "Armadura visível" (28/10/2026): o padrão é toda a armadura aparecendo por baixo do traje. */
const TUDO_VISIVEL: TrajeMostrar = { elmo: true, peito: true, luvas: true, pernas: true };
const ANCORA = { top: 84, left: 300 };
/** As peças de armadura que aparecem por baixo do traje (Armadura visível), e o slot do equipamento de cada uma. */
const ARMADURA: readonly { id: keyof TrajeMostrar; slots: readonly string[]; ghost: string }[] = [
  { id: 'elmo', slots: ['helm'], ghost: 'plate-helm' },
  { id: 'peito', slots: ['chest', 'arms'], ghost: 'plate-chest' },
  { id: 'luvas', slots: ['gloves'], ghost: 'plate-gloves' },
  { id: 'pernas', slots: ['legs'], ghost: 'plate-legs' }
];

/** O traje com todos os lugares preenchidos: comparar e editar sem `undefined`. */
function completo(doc: TrajeDoc | null | undefined, dto: TrajeDto): TrajeDoc {
  const pecas: Record<string, TrajeEscolha> = {};
  for (const s of dto.slots) pecas[s.id] = doc?.pecas?.[s.id] ?? VAZIA;
  // O traje vale sempre: `usar` fica true só para o servidor antigo (o novo ignora).
  return { usar: true, mostrar: { ...TUDO_VISIVEL, ...(doc?.mostrar ?? {}) }, pecas };
}

const mesmo = (a: TrajeDoc, b: TrajeDoc) => JSON.stringify(a) === JSON.stringify(b);

function useIcone(src: string, hue: number): string {
  const [url, setUrl] = useState(src);
  useEffect(() => {
    let vivo = true;
    setUrl(src);
    void iconeDoTraje(src, hue).then(u => { if (vivo) setUrl(u); });
    return () => { vivo = false; };
  }, [src, hue]);
  return url;
}

function IconeDoTraje({ src, hue, className }: { src: string; hue: number; className?: string }) {
  const url = useIcone(src, hue);
  return <img className={className} src={url} alt="" draggable={false} />;
}

const PREFIXO_EXCLUSIVA = 'capa-balron-';
const ehExclusiva = (peca: string | null | undefined) => !!peca && peca.startsWith(PREFIXO_EXCLUSIVA);
/** A skin de cor fixa (techspec-skins, o Natal) tem a cor na arte: o ícone é o dela, sem matiz. A tingível (os mantos) é
 * cinza e segue a cor escolhida, como a peça comum. */
const ehSkin = (dto: TrajeDto, peca: string | null | undefined) => !!peca && !!dto.exclusivas?.some(x => x.id === peca && !!x.set && !x.tingivel);
/** As skins que o personagem pode vestir: as dele, menos as de mulher num homem. */
const skinsVisiveis = (dto: TrajeDto) => (dto.exclusivas ?? []).filter(x => !!x.set && (!x.soMulher || dto.feminino));
/** A peça exclusiva já vem tingida no ícone (`capa-<matiz>.png`, tools/chefe-arte.py). */
const iconeDaExclusiva = (hue: number) => `/sprites/itens/traje/capa-${hue}.png`;
const iconeDaPeca = (peca: string) => `/sprites/itens/traje/${peca}.png`;
const iconeDaTinta = (cor: string) => `/sprites/itens/traje/tinta-${cor}.png`;

/**
 * O guarda-roupa (techspec-guardaroupa): à esquerda o provador, que gira; à direita as peças por lugar do corpo e as
 * cores. As Tintas prime guardadas ficam na segunda aba, com o Retirar. Só o servidor decide o que vale: o provador
 * mostra o rascunho, Vestir salva.
 */
export function GuardaRoupaGump({ dead, provador, onVisual, onChanged, onClose }: Props) {
  const [dto, setDto] = useState<TrajeDto | null>(null);
  const [itens, setItens] = useState<readonly PackItem[]>([]);
  const [rascunho, setRascunho] = useState<TrajeDoc | null>(null);
  const [aba, setAba] = useState<'pecas' | 'prime' | 'skins'>('pecas');
  const [lugar, setLugar] = useState('torso');
  const [idir, setIdir] = useState(0);
  const [retirar, setRetirar] = useState<TrajeCor | null>(null);
  const [extrair, setExtrair] = useState<TrajeExclusiva | null>(null);
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [, setTons] = useState(0);
  const cvRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => { quandoTrajeCarregar(() => setTons(n => n + 1)); }, []);

  useEffect(() => {
    let vivo = true;
    Promise.all([api.getTraje(), api.getPack()])
      .then(([d, pack]) => {
        if (!vivo) return;
        setDto(d);
        setItens(pack.items);
        setRascunho(completo(d.traje, d));
      })
      .catch(err => { if (vivo) setErro(err instanceof ApiError ? err.message : t('traje.naoAbriu')); });
    return () => { vivo = false; };
  }, []);

  const salvo = useMemo(() => (dto ? completo(dto.traje, dto) : null), [dto]);
  const mudou = !!rascunho && !!salvo && !mesmo(rascunho, salvo);
  const guardadas = useMemo(() => new Set(dto?.guardadas ?? []), [dto]);
  const corPorId = useMemo(() => new Map((dto?.cores ?? []).map(c => [c.id, c])), [dto]);
  const pecasVisiveis = useMemo(
    () => (dto?.pecas ?? []).filter(p => !p.soMulher || dto?.feminino),
    [dto]
  );
  const vestidos = useMemo(() => itens.filter(i => !!i.equippedSlot), [itens]);
  const cloth = dto && rascunho ? clothDoTraje(rascunho, dto, dto.feminino, vestidos) : '';
  const dir = DIRECOES_DO_PROVADOR[idir];

  // O useWorld devolve funções novas a cada render: a ref segura a última sem repintar à toa.
  const provadorRef = useRef(provador);
  provadorRef.current = provador;
  useEffect(() => {
    const cv = cvRef.current;
    if (!cv || !dto) return;
    return provadorRef.current(cv, cloth, dir, dto.feminino);
  }, [cloth, dir, dto]);

  useFechar(() => { setRetirar(null); setExtrair(null); }, { ativo: !!retirar || !!extrair, modal: true });

  const hueDe = (cor: string) => corPorId.get(cor)?.hue ?? 0;
  const nomeCor = (cor: string) => nomeDaCorDoTraje(cor, corPorId.get(cor)?.nome ?? cor);
  const escolha = (slot: string) => rascunho?.pecas[slot] ?? VAZIA;
  const editar = (f: (d: TrajeDoc) => TrajeDoc) => setRascunho(d => (d ? f(d) : d));
  const porNoLugar = (slot: string, e: TrajeEscolha) =>
    editar(d => ({ ...d, pecas: { ...d.pecas, [slot]: e } }));
  const girar = (passo: number) => setIdir(i => (i + passo + 8) % 8);
  /** Os lugares onde a cor está, no traje salvo ou no rascunho. */
  /** As skins do personagem (techspec-skins): todas, mesmo as do outro corpo, para poder extrair. */
  const minhasSkins = porRaridade((dto?.exclusivas ?? []).filter(x => !!x.set), x => RARIDADE_DO_SET[x.set ?? '']);
  const novasSkins = minhasSkins.filter(x => x.nova).length;
  /**
   * O jogador clicou para ver a peça exclusiva: o selo "Nova" sai na hora e o servidor guarda (sem esperar e sem
   * travar a tela; se falhar, o selo volta na próxima abertura).
   */
  const marcarVista = (id: string) => {
    if (!dto?.exclusivas?.some(x => x.id === id && x.nova)) return;
    setDto(d => (d ? { ...d, exclusivas: d.exclusivas?.map(x => (x.id === id ? { ...x, nova: false } : x)) } : d));
    void api.marcarSkinVista(id).catch(() => {});
  };
  /** Na aba Skins, clicar na skin leva ao lugar dela e a põe no provador. */
  const verSkin = (x: TrajeExclusiva) => {
    marcarVista(x.id);
    if (!dto || (x.soMulher && !dto.feminino)) return;
    const atual = escolha(x.slot);
    setLugar(x.slot);
    setAba('pecas');
    porNoLugar(x.slot, { peca: x.id, cor: x.tingivel ? atual.cor : NATURAL });
  };
  /** Os lugares onde a skin está, no traje salvo ou no rascunho. */
  const usoDaSkin = (peca: string) => (dto?.slots ?? []).filter(s => salvo?.pecas[s.id]?.peca === peca || rascunho?.pecas[s.id]?.peca === peca);
  const usoDa = (cor: string) => (dto?.slots ?? []).filter(s =>
    (salvo?.pecas[s.id].peca && salvo.pecas[s.id].cor === cor) || (rascunho?.pecas[s.id].peca && rascunho.pecas[s.id].cor === cor));

  const vestir = async () => {
    if (!rascunho || busy) return;
    setBusy(true);
    setErro(null);
    try {
      const d = await api.salvarTraje(rascunho);
      setDto(d);
      setRascunho(completo(d.traje, d));
      const pack = await api.getPack().catch(() => null);
      if (pack) {
        setItens(pack.items);
        onVisual(pack.cloth ?? cloth);
      }
      onChanged(t('traje.vestiu'));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
    } finally {
      setBusy(false);
    }
  };

  const confirmarRetirar = async () => {
    const cor = retirar;
    if (!cor || busy) return;
    setBusy(true);
    setErro(null);
    try {
      const d = await api.retirarTinta(cor.id);
      setDto(d);
      // O rascunho perde a cor também: ela não vale mais.
      editar(r => ({
        ...r,
        pecas: Object.fromEntries(Object.entries(r.pecas).map(([k, e]) => [k, e.cor === cor.id ? { ...e, cor: NATURAL } : e]))
      }));
      setRetirar(null);
      const pack = await api.getPack().catch(() => null);
      if (pack) {
        setItens(pack.items);
        onVisual(pack.cloth ?? null);
      }
      onChanged(t('traje.retirou', { cor: nomeCor(cor.id) }));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
      setRetirar(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmarExtrair = async () => {
    const skin = extrair;
    if (!skin || busy) return;
    setBusy(true);
    setErro(null);
    try {
      const d = await api.extrairSkin(skin.id);
      setDto(d);
      // O rascunho perde a peça também: ela saiu do guarda-roupa.
      editar(r => ({
        ...r,
        pecas: Object.fromEntries(Object.entries(r.pecas).map(([k, e]) => [k, e.peca === skin.id ? VAZIA : e]))
      }));
      setExtrair(null);
      if (!(d.exclusivas ?? []).some(x => !!x.set)) setAba('pecas');
      const pack = await api.getPack().catch(() => null);
      if (pack) {
        setItens(pack.items);
        onVisual(pack.cloth ?? null);
      }
      onChanged(t('traje.skin.extraiu', { peca: nomeDaPecaDoTraje(skin.id, skin.nome) }));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
      setExtrair(null);
    } finally {
      setBusy(false);
    }
  };

  const teclas = (e: KeyboardEvent<HTMLDivElement>) => {
    if (retirar || extrair || (e.target as HTMLElement).closest('input, textarea')) return;
    if (e.key === 'ArrowLeft') { girar(-1); e.preventDefault(); }
    if (e.key === 'ArrowRight') { girar(1); e.preventDefault(); }
  };

  // Armadura visível: ligada se alguma peça aparece; o clique mostra tudo ou esconde tudo. As peças, uma a uma, abaixo.
  const armaduraVisivel = !!rascunho && Object.values(rascunho.mostrar).some(Boolean);
  const usarTraje = (
    <button
      type="button"
      className="gr__usar"
      role="switch"
      aria-checked={armaduraVisivel}
      disabled={!rascunho || dead}
      onClick={() => editar(d => ({ ...d, mostrar: Object.values(d.mostrar).some(Boolean) ? SEM_MOSTRAR : TUDO_VISIVEL }))}
    >
      <span>{t('traje.armaduraVisivel')}</span><i aria-hidden="true" />
    </button>
  );

  const nomeDir = dir === 1 ? t('traje.dirFrente') : dir === 5 ? t('traje.dirCostas') : dir === 3 || dir === 7 ? t('traje.dirLado') : '';
  const corpo = !dto || !rascunho ? (
    <p className={erro ? 'notice' : 'gr-dica'} role={erro ? 'alert' : undefined}>{erro ?? t('traje.abrindo')}</p>
  ) : (
    <>
      <div className="gr-prov">
        <div className="gr-palco">
          <span className="gr-palco__rot">{nomeDir}</span>
          <button type="button" className="gr-gira gr-gira--esq" onClick={() => girar(-1)} aria-label={t('traje.girarEsq')}>⟲</button>
          <canvas ref={cvRef} width={148} height={284} aria-label={t('traje.provador')} />
          <span className="gr-palco__chao" aria-hidden="true" />
          <button type="button" className="gr-gira gr-gira--dir" onClick={() => girar(1)} aria-label={t('traje.girarDir')}>⟳</button>
        </div>
        <div>
          <p className="gr-sub">{t('traje.armaduraAMostra')}</p>
          <div className="gr-chips">
            {ARMADURA.map(a => {
              const item = vestidos.find(i => a.slots.includes(i.equippedSlot ?? ''));
              const ligado = rascunho.mostrar[a.id];
              return (
                <button
                  key={a.id}
                  type="button"
                  className="gr-chip"
                  aria-pressed={ligado}
                  onClick={() => editar(d => ({ ...d, mostrar: { ...d.mostrar, [a.id]: !d.mostrar[a.id] } }))}
                >
                  <img src={iconePara(item?.defId ?? a.ghost)} alt="" draggable={false} />{t(`traje.mostrar.${a.id}`)}
                </button>
              );
            })}
          </div>
          <p className="gr-dica">{t('traje.dicaArmadura')}</p>
        </div>
        <div className="gr-salvar">
          <p className={`gr-salvar__msg${mudou ? ' is-mudou' : ''}`} role={erro ? 'alert' : undefined}>
            {erro ?? t(mudou ? 'traje.naoVestido' : 'traje.vestindo')}
          </p>
          <div className="row">
            <button type="button" className="btn btn--ghost" disabled={!mudou || busy}
              onClick={() => { if (salvo) setRascunho(salvo); setErro(null); }}>
              {t('traje.desfazer')}
            </button>
            <button type="button" className="btn btn--primary" disabled={!mudou || busy || dead} onClick={() => void vestir()}>
              {busy ? t('comum.salvando') : t('traje.vestir')}
            </button>
          </div>
        </div>
      </div>

      <div className="gr-lado">
        <div className="gr-abas" role="tablist">
          <button type="button" role="tab" aria-selected={aba === 'pecas'} onClick={() => setAba('pecas')}>{t('traje.abaPecas')}</button>
          <button type="button" role="tab" aria-selected={aba === 'prime'} onClick={() => setAba('prime')}>
            {t('traje.abaTintas')}<b>{guardadas.size}</b>
          </button>
          {minhasSkins.length > 0 && (
            <button type="button" role="tab" aria-selected={aba === 'skins'} onClick={() => setAba('skins')}>
              {t('traje.abaSkins')}<b>{minhasSkins.length}</b>
              {novasSkins > 0 && <span className="gr-nova" title={t('traje.novaDica')}>{t('traje.nova')}</span>}
            </button>
          )}
        </div>
        {aba === 'pecas' ? (
          <AbaPecas
            dto={dto}
            lugar={lugar}
            setLugar={setLugar}
            escolha={escolha}
            pecas={pecasVisiveis}
            guardadas={guardadas}
            hueDe={hueDe}
            nomeCor={nomeCor}
            porNoLugar={porNoLugar}
            onVista={marcarVista}
            irParaPrime={() => setAba('prime')}
          />
        ) : aba === 'skins' ? (
          <div className="gr-tintas-aba">
            <p className="gr-onde">{t('traje.skin.onde')}</p>
            <div className="gr-tintas">
              {minhasSkins.map(x => {
                const uso = usoDaSkin(x.id).map(s => nomeDoLugarDoTraje(s.id, s.nome));
                const sub = uso.length ? t('traje.emUso', { lugares: uso.join(', ') }) : t('traje.skin.semUso');
                return (
                  <div key={x.id} className={x.nova ? 'gr-tinta gr-tinta--skin is-nova' : 'gr-tinta gr-tinta--skin'}>
                    <button type="button" className="gr-tinta__ver" onClick={() => verSkin(x)} title={t('traje.skin.ver')}>
                      <img src={iconeDaPeca(x.id)} alt="" draggable={false} />
                      <span>
                        <b>
                          {nomeDaPecaDoTraje(x.id, x.nome)}<span className="gr-rar gr-rar--skin">{t(`traje.skin.set.${x.set}`)}</span>
                          {x.nova && <span className="gr-nova">{t('traje.nova')}</span>}
                        </b>
                        <small title={sub}>{sub}</small>
                      </span>
                    </button>
                    <button type="button" className="btn btn--tiny" disabled={busy || dead || !!x.presa} onClick={() => setExtrair(x)}
                      title={x.presa ? t('traje.skin.presa') : undefined}>
                      {t('traje.skin.extrair')}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="gr-tintas-aba">
            <p className="gr-onde">{t('traje.ondeTintas')}</p>
            <div className="gr-tintas">
              {porRaridade(dto.cores.filter(c => !c.livre && c.hue !== 0), c => c.raridade).map(c => {
                const tem = guardadas.has(c.id);
                const uso = tem ? usoDa(c.id).map(s => nomeDoLugarDoTraje(s.id, s.nome)) : [];
                const sub = !tem ? t('traje.naoGuardada') : uso.length ? t('traje.emUso', { lugares: uso.join(', ') }) : t('traje.guardadaSemUso');
                return (
                  <div key={c.id} className={`gr-tinta${tem ? '' : ' is-trava'}`}>
                    <img src={iconeDaTinta(c.id)} alt="" draggable={false} />
                    <span>
                      <b>{nomeCor(c.id)}{c.raridade && <span className="gr-rar" data-r={c.raridade}>{t(`traje.raridade.${c.raridade}`)}</span>}</b>
                      <small title={sub}>{sub}</small>
                    </span>
                    {tem && (
                      <button type="button" className="btn btn--tiny" disabled={busy || dead} onClick={() => setRetirar(c)}>
                        {t('traje.retirar')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {extrair && (
        <div className="gr-modal" role="dialog" aria-modal="true" aria-labelledby="gr-extrair-t">
          <div className="gr-modal__c">
            <h3 id="gr-extrair-t"><img src={iconeDaPeca(extrair.id)} alt="" />{t('traje.skin.extrairTitulo', { peca: nomeDaPecaDoTraje(extrair.id, extrair.nome) })}</h3>
            <p>{t('traje.skin.extrairVai')}</p>
            {usoDaSkin(extrair.id).length > 0 && (
              <p>{t('traje.skin.extrairSai', { lugares: usoDaSkin(extrair.id).map(s => nomeDoLugarDoTraje(s.id, s.nome).toLowerCase()).join(', ') })}</p>
            )}
            <p>{t('traje.skin.extrairDepois')}</p>
            <div className="row">
              <button type="button" className="btn btn--ghost" onClick={() => setExtrair(null)} disabled={busy}>{t('comum.cancelar')}</button>
              <button type="button" className="btn btn--danger" onClick={() => void confirmarExtrair()} disabled={busy} data-foco-inicial>
                {t('traje.skin.extrair')}
              </button>
            </div>
          </div>
        </div>
      )}

      {retirar && (
        <div className="gr-modal" role="dialog" aria-modal="true" aria-labelledby="gr-retirar-t">
          <div className="gr-modal__c">
            <h3 id="gr-retirar-t"><img src={iconeDaTinta(retirar.id)} alt="" />{t('traje.retirarTitulo', { cor: nomeCor(retirar.id) })}</h3>
            <p>{t('traje.retirarVolta', { cor: nomeCor(retirar.id) })}</p>
            {usoDa(retirar.id).length > 0 && (
              <p>{t('traje.retirarNatural', { lugares: usoDa(retirar.id).map(s => nomeDoLugarDoTraje(s.id, s.nome).toLowerCase()).join(', ') })}</p>
            )}
            <p>{t('traje.retirarDepois')}</p>
            <div className="row">
              <button type="button" className="btn btn--ghost" onClick={() => setRetirar(null)} disabled={busy}>{t('comum.cancelar')}</button>
              <button type="button" className="btn btn--danger" onClick={() => void confirmarRetirar()} disabled={busy} data-foco-inicial>
                {t('traje.retirar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <GumpWindow
      title={t('traje.titulo')}
      anchor={ANCORA}
      center
      className="win--gr gr"
      headLead={<IcGuardaRoupa className="gr__ic" />}
      headExtra={usarTraje}
      corpoClasse="gr__corpo"
      onClose={onClose}
    >
      <div className="gr__grade" onKeyDown={teclas}>{corpo}</div>
    </GumpWindow>
  );
}

interface AbaPecasProps {
  dto: TrajeDto;
  lugar: string;
  setLugar(id: string): void;
  escolha(slot: string): TrajeEscolha;
  pecas: TrajeDto['pecas'];
  guardadas: ReadonlySet<string>;
  hueDe(cor: string): number;
  nomeCor(cor: string): string;
  porNoLugar(slot: string, e: TrajeEscolha): void;
  /** Clicou numa peça exclusiva com o selo "Nova": ela passa a vista. */
  onVista(id: string): void;
  irParaPrime(): void;
}

function AbaPecas({ dto, lugar, setLugar, escolha, pecas, guardadas, hueDe, nomeCor, porNoLugar, onVista, irParaPrime }: AbaPecasProps) {
  // Os lugares só de skin (braços, mãos, cajado) aparecem quando o personagem tem uma skin para eles.
  const skins = skinsVisiveis(dto);
  const slots = dto.slots.filter(s => !s.soSkin || skins.some(x => x.slot === s.id) || !!escolha(s.id).peca);
  const slot = slots.find(s => s.id === lugar) ?? slots[0];
  const atual = escolha(slot.id);
  const doLugar = pecas.filter(p => p.slot === slot.id);
  const pecaAtual = dto.pecas.find(p => p.id === atual.peca);
  const exclusivas = (dto.exclusivas ?? []).filter(x => x.slot === slot.id && (!x.set || !x.soMulher || dto.feminino));
  const exclusivaAtual = exclusivas.find(x => x.id === atual.peca) ?? null;
  const corAtual = dto.cores.find(c => c.id === atual.cor);
  const basicas = dto.cores.filter(c => !c.prime);
  const basicasTrancadas = basicas.filter(c => !c.livre).length;
  const primes = porRaridade(dto.cores.filter(c => c.prime), c => c.raridade);
  const escolherCor = (cor: string) => porNoLugar(slot.id, { peca: atual.peca ?? doLugar[0]?.id ?? null, cor });

  return (
    <>
      <div className="gr-slots" role="tablist" aria-label={t('traje.lugares')}>
        {slots.map(s => {
          const e = escolha(s.id);
          const hue = hueDe(e.cor);
          const prime = !!dto.cores.find(c => c.id === e.cor)?.prime;
          const nome = nomeDoLugarDoTraje(s.id, s.nome);
          // Um lugar com peça exclusiva ainda não vista ganha o selo, para o jogador achar onde ela está.
          const temNova = (dto.exclusivas ?? []).some(x => x.nova && x.slot === s.id && (!x.set || !x.soMulher || dto.feminino));
          return (
            <button key={s.id} type="button" className="gr-slot" role="tab" aria-selected={s.id === slot.id}
              title={nome} onClick={() => setLugar(s.id)}>
              <span className="gr-slot__ico">
                {ehExclusiva(e.peca)
                  ? <img src={iconeDaExclusiva(dto.exclusivas?.find(x => x.id === e.peca)?.hue ?? 0)} alt="" draggable={false} />
                  : ehSkin(dto, e.peca) ? <img src={iconeDaPeca(e.peca!)} alt="" draggable={false} />
                  : e.peca ? <IconeDoTraje src={iconeDaPeca(e.peca)} hue={hue} /> : <span className="gr-slot__vazio" />}
              </span>
              {e.peca && !ehExclusiva(e.peca) && !ehSkin(dto, e.peca) && e.cor !== NATURAL && (
                <i className="gr-slot__cor" style={{ background: prime ? degradeDaMatiz(hue) : corDaMatiz(hue) }} />
              )}
              <small>{nome}</small>
              {temNova && <span className="gr-nova gr-nova--canto">{t('traje.nova')}</span>}
            </button>
          );
        })}
      </div>

      <div className="gr-caixa">
        <p className="gr-sub">
          {nomeDoLugarDoTraje(slot.id, slot.nome)}
          <span>{exclusivaAtual ? (exclusivaAtual.set ? nomeDaPecaDoTraje(exclusivaAtual.id, exclusivaAtual.nome) : nomeDoItem(exclusivaAtual.id, exclusivaAtual.nome)) : pecaAtual ? nomeDaPecaDoTraje(pecaAtual.id, pecaAtual.nome) : t('traje.nada')}</span>
        </p>
        <div className="gr-pecas">
          <button type="button" className="gr-peca gr-peca--nada" aria-pressed={!atual.peca}
            onClick={() => porNoLugar(slot.id, { ...atual, peca: null })}>
            <span className="gr-peca__ico" aria-hidden="true">∅</span><b>{t('traje.nada')}</b>
          </button>
          {doLugar.map(p => (
            <button key={p.id} type="button" className="gr-peca" aria-pressed={atual.peca === p.id}
              onClick={() => porNoLugar(slot.id, { ...atual, peca: p.id, cor: exclusivaAtual && !exclusivaAtual.tingivel ? NATURAL : atual.cor })}>
              <span className="gr-peca__ico"><IconeDoTraje src={iconeDaPeca(p.id)} hue={exclusivaAtual && !exclusivaAtual.tingivel ? 0 : hueDe(atual.cor)} /></span>
              <b>{nomeDaPecaDoTraje(p.id, p.nome)}</b>
            </button>
          ))}
          {exclusivas.map(x => x.set ? (
            <button key={x.id} type="button" className="gr-peca gr-peca--exclusiva gr-peca--skin" aria-pressed={atual.peca === x.id}
              title={t(x.tingivel ? 'traje.skin.dicaTingivel' : 'traje.skin.dica')}
              onClick={() => { onVista(x.id); porNoLugar(slot.id, { peca: x.id, cor: x.tingivel && !(exclusivaAtual && !exclusivaAtual.tingivel) ? atual.cor : NATURAL }); }}>
              {x.nova && <span className="gr-nova gr-nova--canto">{t('traje.nova')}</span>}
              <span className="gr-peca__ico">
                {x.tingivel
                  ? <IconeDoTraje src={iconeDaPeca(x.id)} hue={exclusivaAtual && !exclusivaAtual.tingivel ? 0 : hueDe(atual.cor)} />
                  : <img src={iconeDaPeca(x.id)} alt="" draggable={false} />}
              </span>
              <b>{nomeDaPecaDoTraje(x.id, x.nome)}</b>
              <small className="gr-peca__selo">
                {t('traje.skin.selo', { set: t(`traje.skin.set.${x.set}`) })}{x.bonus > 0 ? ` · +${x.bonus}` : ''}
              </small>
            </button>
          ) : (
            <button key={x.id} type="button" className="gr-peca gr-peca--exclusiva" aria-pressed={atual.peca === x.id}
              title={t('traje.exclusiva.dica', { n: x.bonus })}
              onClick={() => { onVista(x.id); porNoLugar(slot.id, { peca: x.id, cor: NATURAL }); }}>
              {x.nova && <span className="gr-nova gr-nova--canto">{t('traje.nova')}</span>}
              <span className="gr-peca__ico"><img src={iconeDaExclusiva(x.hue)} alt="" draggable={false} /></span>
              <b>{nomeDoItem(x.id, x.nome)}</b>
              <small className="gr-peca__selo">🔒 {t('traje.exclusiva.unica')} · +{x.bonus}</small>
            </button>
          ))}
        </div>
        {exclusivaAtual && exclusivaAtual.bonus > 0 && <p className="gr-dica gr-dica--exclusiva">{t('traje.exclusiva.bonus', { n: exclusivaAtual.bonus })}</p>}
        {slot.id === 'arma' && <p className="gr-dica">{t('traje.skin.cajado')}</p>}
      </div>

      {(exclusivaAtual ? !!exclusivaAtual.tingivel : !slot.soSkin) && <div className="gr-caixa">
        <p className="gr-sub">
          {t('traje.cor')}
          <span>{nomeCor(atual.cor)}{corAtual?.prime ? ` · ${t('traje.prime')}` : ''}</span>
        </p>
        <div className="gr-cores" role="radiogroup" aria-label={t('traje.basicas')}>
          {basicas.map(c => {
            // A Natural e três básicas são livres; as outras pedem a tinta comum guardada (Baú do Balron).
            const tem = !!c.livre || guardadas.has(c.id);
            const rotulo = `${nomeCor(c.id)}${tem ? '' : ` — ${t('traje.trava')}`}`;
            return (
              <button key={c.id} type="button" role="radio" aria-checked={atual.cor === c.id}
                className={`gr-cor${c.hue ? '' : ' gr-cor--natural'}${tem ? '' : ' is-trava'}`} title={rotulo} aria-label={rotulo}
                style={{ '--c': corDaMatiz(c.hue) } as CSSProperties} onClick={() => (tem ? escolherCor(c.id) : irParaPrime())} />
            );
          })}
        </div>
        {basicasTrancadas > 0 && (
          <p className="gr-dica">{t('traje.basicasDe', { n: basicas.filter(c => c.livre || guardadas.has(c.id)).length, total: basicas.length })}</p>
        )}
        <p className="gr-sub gr-sub--prime">
          {t('traje.prime')}
          <span>{t('traje.guardadasDe', { n: primes.filter(c => guardadas.has(c.id)).length, total: primes.length })}</span>
        </p>
        <div className="gr-cores" role="radiogroup" aria-label={t('traje.primes')}>
          {primes.map(c => {
            const tem = guardadas.has(c.id);
            const rotulo = `${nomeCor(c.id)} · ${t(`traje.raridade.${c.raridade ?? 'rara'}`)}${tem ? '' : ` — ${t('traje.trava')}`}`;
            return (
              <button key={c.id} type="button" role="radio" aria-checked={atual.cor === c.id}
                className={`gr-cor gr-cor--prime${tem ? '' : ' is-trava'}`} data-r={c.raridade ?? undefined}
                title={rotulo} aria-label={rotulo}
                style={{ background: degradeDaMatiz(c.hue) }}
                onClick={() => (tem ? escolherCor(c.id) : irParaPrime())} />
            );
          })}
        </div>
        <p className="gr-dica">{t('traje.dicaCores')}</p>
      </div>}
      {exclusivaAtual && !exclusivaAtual.tingivel && (
        <div className="gr-caixa">
          <p className="gr-sub">{t('traje.cor')}<span>{t('traje.exclusiva.corFixa')}</span></p>
          <p className="gr-dica">{t(exclusivaAtual.set ? 'traje.skin.semTinta' : 'traje.exclusiva.semTinta')}</p>
        </div>
      )}
    </>
  );
}
